import { useLocation, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, ExternalLink, LogOut, AlertTriangle, Shield, ShieldCheck, ShieldAlert, TrendingUp } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_readiness-check-27/artifacts/el8npnpt_CXOlogo.png";

const RAG_COLORS = {
  RED: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-800 border-red-300', bar: 'bg-red-500' },
  ORANGE: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-800 border-orange-300', bar: 'bg-orange-500' },
  AMBER: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-800 border-amber-300', bar: 'bg-amber-500' },
  YELLOW: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-800 border-yellow-300', bar: 'bg-yellow-500' },
  GREEN: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-800 border-green-300', bar: 'bg-green-500' },
  TEAL: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', badge: 'bg-teal-100 text-teal-800 border-teal-300', bar: 'bg-teal-500' },
};

const PillarCard = ({ pillar, data }) => {
  const colors = RAG_COLORS[data?.rag] || RAG_COLORS.RED;
  const isNewPillar = ['P9', 'P10', 'P11'].includes(pillar?.code);
  const maxScore = isNewPillar ? 24 : (data?.max || 24);
  const displayScore = isNewPillar ? data?.raw : data?.raw;

  return (
    <div
      className={`${colors.bg} border ${colors.border} rounded-sm p-4 transition-all hover:shadow-md`}
      data-testid={`pillar-card-${pillar?.code}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-[700] text-slate-400 tracking-wider">{pillar?.code}</span>
        <Badge className={`${colors.badge} border text-xs`}>{data?.rag}</Badge>
      </div>
      <h3 className="text-sm font-[700] text-[#003366] mb-3 leading-tight min-h-[2.5rem]">{pillar?.name}</h3>
      <div className="mb-2">
        <div className="flex items-end gap-1">
          <span className={`text-2xl font-[900] ${colors.text}`}>{displayScore}</span>
          <span className="text-sm text-slate-400 mb-0.5">/{maxScore}</span>
        </div>
      </div>
      <div className="w-full bg-slate-200 rounded-full h-1.5 mb-2">
        <div className={`${colors.bar} h-1.5 rounded-full transition-all`} style={{ width: `${Math.min(data?.pct || 0, 100)}%` }} />
      </div>
      {data?.band ? (
        <p className={`text-xs font-[600] ${colors.text}`}>{data.band}</p>
      ) : (
        <p className="text-xs text-slate-500">{data?.priority?.split('—')[0]?.trim() || ''}</p>
      )}
    </div>
  );
};

const SDFBadge = ({ sdfFlag, p9Score }) => {
  let text, colorClass;

  if (sdfFlag) {
    text = 'HIGH RISK — Automated Profiling Detected';
    colorClass = 'bg-red-100 text-red-800 border-red-300';
  } else if (p9Score <= 6) {
    text = 'SDF Designation: PROBABLE';
    colorClass = 'bg-red-100 text-red-800 border-red-300';
  } else if (p9Score <= 12) {
    text = 'SDF Designation: LIKELY';
    colorClass = 'bg-orange-100 text-orange-800 border-orange-300';
  } else if (p9Score <= 18) {
    text = 'SDF Designation: POSSIBLE';
    colorClass = 'bg-amber-100 text-amber-800 border-amber-300';
  } else {
    text = 'SDF Designation: UNLIKELY';
    colorClass = 'bg-green-100 text-green-800 border-green-300';
  }

  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm border ${colorClass}`} data-testid="sdf-risk-badge">
      <ShieldAlert className="h-4 w-4" />
      <span className="text-sm font-[700]">{text}</span>
    </div>
  );
};

const SubmissionCompletePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { assessmentRef } = location.state || {};
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.get(`${API}/api/assessment/my-results`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setResults(resp.data);
      } catch {
        // Results may not be available yet
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchResults();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const scoring = results?.scoring || {};
  const pillars = results?.pillars || [];
  const pillarScores = scoring.pillar_scores_json ? JSON.parse(scoring.pillar_scores_json)?.pillars || {} : {};
  const p9Data = pillarScores['P9'] || {};
  const penaltyRag = scoring.readiness_rag || scoring.penalty_risk || '';
  const isPenaltyDanger = ['RED', 'ORANGE'].includes(penaltyRag);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="CodeXero" className="h-10" />
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-btn"
            className="text-slate-600 hover:text-red-600"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 py-10 px-6">
        <div className="max-w-6xl mx-auto">

          {/* Success Banner */}
          <div className="bg-white border border-slate-200 rounded-sm p-8 mb-8 text-center shadow-[0_4px_24px_rgba(0,51,102,0.06)]">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-[800] text-[#003366] mb-3" data-testid="submission-heading">
              Assessment Submitted Successfully
            </h1>
            <p className="text-lg text-slate-600 mb-4" data-testid="thank-you-message">
              Thank you, <span className="font-semibold">{user?.first_name}</span>. Your DPDP Readiness Assessment for{' '}
              <span className="font-semibold">{user?.org_name}</span> has been received.
            </p>
            <div className="inline-block bg-slate-50 border border-slate-200 rounded-sm px-6 py-3">
              <p className="text-sm text-slate-500 mb-1">Assessment ID</p>
              <p className="text-lg font-[700] text-[#003366]" data-testid="assessment-ref">
                {assessmentRef || results?.assessment?.assessment_ref_id || 'CX0-DPDP-2026-XXXX'}
              </p>
            </div>
          </div>

          {/* Scoring Dashboard */}
          {!loading && results && (
            <>
              {/* Overall Score Section */}
              <div className="bg-white border border-slate-200 rounded-sm p-8 mb-8 shadow-[0_4px_24px_rgba(0,51,102,0.06)]">
                <h2 className="text-xl font-[800] text-[#003366] mb-6">Overall DPDP Readiness</h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                  {/* Overall Score Gauge */}
                  <div className="text-center" data-testid="overall-score-section">
                    <div className="relative w-40 h-40 mx-auto mb-4">
                      <svg viewBox="0 0 120 120" className="w-full h-full">
                        <circle cx="60" cy="60" r="52" fill="none" stroke="#e2e8f0" strokeWidth="10" />
                        <circle
                          cx="60" cy="60" r="52"
                          fill="none"
                          stroke={scoring.readiness_rag === 'GREEN' || scoring.readiness_rag === 'TEAL' ? '#22c55e' : scoring.readiness_rag === 'AMBER' ? '#f59e0b' : scoring.readiness_rag === 'ORANGE' ? '#f97316' : '#ef4444'}
                          strokeWidth="10"
                          strokeDasharray={`${(scoring.overall_score || 0) * 3.267} 326.7`}
                          strokeLinecap="round"
                          transform="rotate(-90 60 60)"
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-3xl font-[900] text-[#003366]" data-testid="overall-score-pct">{scoring.overall_score}%</span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-500" data-testid="raw-score-display">
                      Weighted Compliance Score
                    </p>

                    {/* SDF Risk Badge */}
                    <div className="mt-4" data-testid="sdf-section">
                      <SDFBadge sdfFlag={scoring.sdf_flag} p9Score={p9Data.raw || 0} />
                    </div>
                  </div>

                  {/* Readiness Band */}
                  <div className="text-center flex flex-col justify-center" data-testid="readiness-band-section">
                    <p className="text-sm text-slate-500 mb-2">Readiness Band</p>
                    <p className="text-2xl font-[900] text-[#003366] mb-2" data-testid="readiness-band-label">
                      {scoring.readiness_band || scoring.maturity_label}
                    </p>
                    <p className="text-sm text-slate-500 italic mb-4">{scoring.maturity_description}</p>
                    {/* Penalty Exposure Line */}
                    <p
                      className={`text-base font-[700] ${isPenaltyDanger ? 'text-red-600' : 'text-green-600'}`}
                      data-testid="penalty-exposure-line"
                    >
                      {scoring.penalty_exposure_text || scoring.penalty_exposure}
                    </p>
                  </div>

                  {/* Gap Summary */}
                  <div className="flex flex-col justify-center" data-testid="gap-summary-section">
                    <p className="text-sm text-slate-500 mb-4 text-center">Gap Summary</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-center">
                        <p className="text-2xl font-[900] text-red-600">{scoring.total_critical}</p>
                        <p className="text-xs text-red-600 font-[600]">Critical</p>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-center">
                        <p className="text-2xl font-[900] text-amber-600">{scoring.total_major}</p>
                        <p className="text-xs text-amber-600 font-[600]">Major</p>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-3 text-center">
                        <p className="text-2xl font-[900] text-yellow-600">{scoring.total_minor}</p>
                        <p className="text-xs text-yellow-600 font-[600]">Minor</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pillar Score Cards Grid */}
              <div className="bg-white border border-slate-200 rounded-sm p-8 mb-8 shadow-[0_4px_24px_rgba(0,51,102,0.06)]">
                <h2 className="text-xl font-[800] text-[#003366] mb-6">Pillar-wise Compliance Scores</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="pillar-cards-grid">
                  {pillars.map((pillar) => (
                    <PillarCard
                      key={pillar.code}
                      pillar={pillar}
                      data={pillarScores[pillar.code]}
                    />
                  ))}
                </div>
              </div>

              {/* Next Steps */}
              <div className="bg-white border border-slate-200 rounded-sm p-8 mb-8 shadow-[0_4px_24px_rgba(0,51,102,0.06)]">
                <h2 className="text-xl font-[800] text-[#003366] mb-4">What Happens Next?</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex gap-4">
                    <div className="w-10 h-10 bg-[#003366] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-[800] text-sm">1</span>
                    </div>
                    <div>
                      <p className="font-[700] text-[#003366] mb-1">Detailed Report</p>
                      <p className="text-sm text-slate-500">The CodeXero team will generate your comprehensive DPDP compliance report within 3-5 business days.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-10 h-10 bg-[#003366] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-[800] text-sm">2</span>
                    </div>
                    <div>
                      <p className="font-[700] text-[#003366] mb-1">Gap Analysis</p>
                      <p className="text-sm text-slate-500">Each gap will be mapped to specific DPDP Act sections with remediation recommendations.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-10 h-10 bg-[#003366] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-[800] text-sm">3</span>
                    </div>
                    <div>
                      <p className="font-[700] text-[#003366] mb-1">Compliance Roadmap</p>
                      <p className="text-sm text-slate-500">A phased roadmap with timelines to achieve full compliance before the May 2027 deadline.</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Loading State */}
          {loading && (
            <div className="bg-white border border-slate-200 rounded-sm p-12 mb-8 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#003366] mx-auto mb-4" />
              <p className="text-slate-500">Loading your results...</p>
            </div>
          )}

          {/* Contact and Actions */}
          <div className="bg-white border border-slate-200 rounded-sm p-8 text-center shadow-[0_4px_24px_rgba(0,51,102,0.06)]">
            <p className="text-slate-500 mb-6">
              Questions? Contact us at{' '}
              <a href="mailto:support@codexero.ai" className="text-[#008080] hover:underline font-medium">
                support@codexero.ai
              </a>
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
              <a href="https://www.codexero.ai" target="_blank" rel="noopener noreferrer" className="flex-1">
                <Button
                  data-testid="visit-website-btn"
                  className="w-full bg-[#003366] hover:bg-[#002244] text-white py-6 rounded-sm"
                >
                  Visit CodeXero
                  <ExternalLink className="h-4 w-4 ml-2" />
                </Button>
              </a>
              <Button
                variant="outline"
                onClick={handleLogout}
                data-testid="logout-btn-main"
                className="flex-1 py-6 rounded-sm"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Log Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#003366] text-white py-6 mt-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <img src={LOGO_URL} alt="CodeXero" className="h-8 brightness-0 invert mx-auto mb-4" />
          <p className="text-slate-400 text-sm">&copy; CodeXero {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
};

export default SubmissionCompletePage;
